import type { NodeOverlay } from './canvasTypes'
import { hitTestNodeOverlays } from './graphGeometry'
import {
  type GraphLiveAppearance,
  hitTestLiveValueAppearances,
} from './graphInstrumentation'

export type ClientPoint = { x: number; y: number }

export function hitTestClientPoint(
  overlays: NodeOverlay[],
  svg: SVGSVGElement | null,
  point: ClientPoint,
  liveValueAppearances: GraphLiveAppearance[]
): { overlay: NodeOverlay | undefined } | undefined {
  const matrix = svg?.getScreenCTM()
  if (!matrix || !matrix.a || !matrix.d) return undefined
  const canvasPoint = new DOMPoint(point.x, point.y).matrixTransform(
    matrix.inverse()
  )
  const pointOnCanvas = {
    denormalized_x: canvasPoint.x,
    denormalized_y: canvasPoint.y,
  }
  return {
    overlay:
      hitTestLiveValueAppearances(liveValueAppearances, pointOnCanvas) ??
      hitTestNodeOverlays(overlays, pointOnCanvas, {
        scale_x: Math.hypot(matrix.a, matrix.b),
        scale_y: Math.hypot(matrix.c, matrix.d),
      }),
  }
}

function isTooltipTarget(target: EventTarget | null): boolean {
  return (
    // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM marker used to identify graph tooltip elements.
    target instanceof Element && target.closest('[data-graph-tooltip]') !== null
  )
}

type InspectionHandlers = {
  move: (point: ClientPoint, buttons: number) => void
  leave: () => void
  down: () => void
  up: () => void
  escape: () => void
  suspend: () => void
  isDragging: () => boolean
}

export function listenForNodeOverlayInspection(
  container: HTMLDivElement,
  handlers: InspectionHandlers
): () => void {
  const move = (event: PointerEvent): void => {
    if (isTooltipTarget(event.target) || event.pointerType === 'touch') return
    handlers.move({ x: event.clientX, y: event.clientY }, event.buttons)
  }
  const leave = (event: PointerEvent): void => {
    if (!isTooltipTarget(event.relatedTarget)) handlers.leave()
  }
  const down = (event: PointerEvent): void => {
    if (!isTooltipTarget(event.target)) handlers.down()
  }
  const up = (event: PointerEvent): void => {
    if (isTooltipTarget(event.target) && !handlers.isDragging()) return
    handlers.up()
  }
  const suspendOutsideTooltip = (event: Event): void => {
    if (!isTooltipTarget(event.target)) handlers.suspend()
  }
  const escape = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') handlers.escape()
  }
  container.addEventListener('pointermove', move, true)
  container.addEventListener('pointerleave', leave)
  container.addEventListener('pointerdown', down, true)
  container.addEventListener('wheel', suspendOutsideTooltip, {
    passive: true,
    capture: true,
  })
  container.addEventListener('scroll', suspendOutsideTooltip, true)
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', up)
  window.addEventListener('keydown', escape)
  const observer = new ResizeObserver(handlers.suspend)
  observer.observe(container)
  return (): void => {
    container.removeEventListener('pointermove', move, true)
    container.removeEventListener('pointerleave', leave)
    container.removeEventListener('pointerdown', down, true)
    container.removeEventListener('wheel', suspendOutsideTooltip, true)
    container.removeEventListener('scroll', suspendOutsideTooltip, true)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', up)
    window.removeEventListener('keydown', escape)
    observer.disconnect()
  }
}
